import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { blogPosts } from "@/mocks/homeData";

export default function BlogPage() {
  const { t, i18n } = useTranslation();

  const localeMap: Record<string, string> = { en: "en-GB", fr: "fr-FR", de: "de-DE" };
  const dateLocale = localeMap[i18n.language] || "en-GB";

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-16 px-4 md:px-6 max-w-6xl mx-auto">
        <div className="text-center">
          <span className="inline-block px-4 py-1.5 border border-secondary-400/30 text-secondary-300 text-xs md:text-sm font-medium rounded-full whitespace-nowrap mb-4">
            {t("blog.badge")}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 tracking-tight">
            {t("blog.heading")}
          </h1>
          <p className="text-sm md:text-base text-background-50/60 mt-4 max-w-xl mx-auto leading-relaxed">
            {t("blog.description")}
          </p>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="px-4 md:px-6 pb-16 md:pb-24 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {blogPosts.map((post) => (
            <article
              key={post.id}
              className="group bg-background-900/60 rounded-2xl border border-background-700 overflow-hidden transition-all duration-300 hover:border-primary-400/30 hover:-translate-y-1"
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={post.image}
                  alt={t(`posts.${post.key}.title`)}
                  title={t(`posts.${post.key}.title`)}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] md:text-xs font-medium text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full whitespace-nowrap">
                    {t(`posts.${post.key}.category`)}
                  </span>
                  <span className="text-[10px] md:text-xs text-background-50/40">
                    {new Date(post.date).toLocaleDateString(dateLocale, { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                <h3 className="font-heading text-sm md:text-base font-semibold text-background-50 leading-snug line-clamp-2 group-hover:text-primary-400 transition-colors">
                  {t(`posts.${post.key}.title`)}
                </h3>
                <p className="text-xs md:text-sm text-background-50/50 mt-2 line-clamp-3 leading-relaxed">
                  {t(`posts.${post.key}.excerpt`)}
                </p>
                <div className="mt-3 pt-3 border-t border-background-700/50">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-xs font-medium text-primary-400 flex items-center gap-1 group-hover:gap-2 transition-all cursor-pointer"
                  >
                    {t("blog.readMore")}
                    <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}